import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Paperclip, Plus, Trash2, FileImage, FileText, FileSpreadsheet, File, FileArchive } from 'lucide-react';
import toast from 'react-hot-toast';
import {
    getTaskAttachments, uploadTaskAttachment, getAttachmentDownloadUrl, deleteTaskAttachment,
    type TaskAttachment,
} from '../packages/api-client/pm-api';
import { getErrorMessage } from '../packages/api-client';

const MAX_BYTES = 25 * 1024 * 1024;
const ALLOWED_EXT = new Set([
    'jpg', 'jpeg', 'png', 'gif', 'webp',
    'pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'txt', 'csv', 'zip',
]);

function formatSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// Thumbnail renders an inline image preview for image attachments (lazily
// fetching a presigned inline URL), and falls back to the type icon otherwise.
function Thumbnail({ taskId, att, onClick }: { taskId: string; att: TaskAttachment; onClick: () => void }) {
    const isImage = att.content_type.startsWith('image/');
    const [src, setSrc] = useState<string | null>(null);
    const [failed, setFailed] = useState(false);

    useEffect(() => {
        if (!isImage) return;
        let cancelled = false;
        getAttachmentDownloadUrl(taskId, att.id, { inline: true })
            .then(url => { if (!cancelled) setSrc(url); })
            .catch(() => { if (!cancelled) setFailed(true); });
        return () => { cancelled = true; };
    }, [taskId, att.id, isImage]);

    const box: React.CSSProperties = {
        width: 40, height: 40, flexShrink: 0, borderRadius: 6, cursor: 'pointer',
        display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.2rem',
        overflow: 'hidden', background: 'var(--color-surface-hover, rgba(0,0,0,0.04))',
    };

    if (isImage && src && !failed) {
        return (
            <img
                src={src}
                alt={att.filename}
                onClick={onClick}
                onError={() => setFailed(true)}
                style={{ ...box, objectFit: 'cover' }}
            />
        );
    }
    return <span style={box} onClick={onClick} aria-hidden="true">{fileIcon(att.content_type)}</span>;
}

function fileIcon(contentType: string): React.ReactNode {
    if (contentType.startsWith('image/')) return <FileImage size={20} aria-hidden="true" />;
    if (contentType === 'application/pdf') return <FileText size={20} aria-hidden="true" />;
    if (contentType.includes('spreadsheet') || contentType.includes('excel') || contentType === 'text/csv') return <FileSpreadsheet size={20} aria-hidden="true" />;
    if (contentType.includes('presentation') || contentType.includes('powerpoint')) return <FileText size={20} aria-hidden="true" />;
    if (contentType.includes('word') || contentType === 'text/plain') return <FileText size={20} aria-hidden="true" />;
    if (contentType === 'application/zip') return <FileArchive size={20} aria-hidden="true" />;
    return <File size={20} aria-hidden="true" />;
}

/** readOnly hides upload and delete (tasks:create / tasks:delete); downloads stay. */
export function AttachmentsSection({ taskId, readOnly = false }: { taskId: string; readOnly?: boolean }) {
    const [attachments, setAttachments] = useState<TaskAttachment[]>([]);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [dragOver, setDragOver] = useState(false);
    const fileInput = useRef<HTMLInputElement>(null);

    const load = useCallback(async () => {
        try {
            setAttachments(await getTaskAttachments(taskId));
        } catch (err) {
            toast.error(getErrorMessage(err));
        } finally {
            setLoading(false);
        }
    }, [taskId]);

    useEffect(() => { load(); }, [load]);

    const uploadFiles = useCallback(async (files: FileList | File[]) => {
        const list = Array.from(files);
        if (list.length === 0) return;
        setUploading(true);
        try {
            for (const file of list) {
                const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
                if (!ALLOWED_EXT.has(ext)) {
                    toast.error(`${file.name}: unsupported file type`);
                    continue;
                }
                if (file.size > MAX_BYTES) {
                    toast.error(`${file.name}: exceeds 25 MB`);
                    continue;
                }
                await uploadTaskAttachment(taskId, file);
            }
            await load();
        } catch (err) {
            toast.error(getErrorMessage(err));
        } finally {
            setUploading(false);
            if (fileInput.current) fileInput.current.value = '';
        }
    }, [taskId, load]);

    const handleDownload = async (att: TaskAttachment) => {
        try {
            const url = await getAttachmentDownloadUrl(taskId, att.id);
            window.open(url, '_blank', 'noopener,noreferrer');
        } catch (err) {
            toast.error(getErrorMessage(err));
        }
    };

    const handleDelete = async (att: TaskAttachment) => {
        if (!confirm(`Delete "${att.filename}"?`)) return;
        try {
            await deleteTaskAttachment(taskId, att.id);
            setAttachments(prev => prev.filter(a => a.id !== att.id));
            toast.success('Attachment deleted');
        } catch (err) {
            toast.error(getErrorMessage(err));
        }
    };

    return (
        <div className="card" style={{ padding: 'var(--space-4)' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'var(--space-3)' }}>
                <span style={{ fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 'var(--space-2)' }}><Paperclip size={16} aria-hidden="true" /> Attachments {attachments.length > 0 && `(${attachments.length})`}</span>
                {!readOnly && <button
                    className="btn btn-secondary btn-sm"
                    onClick={() => fileInput.current?.click()}
                    disabled={uploading}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--space-2)' }}
                >
                    {uploading ? 'Uploading…' : (<><Plus size={14} aria-hidden="true" /> Add file</>)}
                </button>}
                <input
                    ref={fileInput}
                    type="file"
                    multiple
                    style={{ display: 'none' }}
                    onChange={e => e.target.files && uploadFiles(e.target.files)}
                    aria-label="Upload attachment"
                />
            </div>

            {/* Drag-and-drop zone */}
            {!readOnly && <div
                onDragOver={e => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={e => { e.preventDefault(); setDragOver(false); if (e.dataTransfer.files) uploadFiles(e.dataTransfer.files); }}
                onClick={() => fileInput.current?.click()}
                style={{
                    border: `2px dashed ${dragOver ? 'var(--color-teal)' : 'var(--color-border)'}`,
                    borderRadius: 8, padding: 'var(--space-3)', textAlign: 'center',
                    color: 'var(--color-text-muted)', fontSize: 'var(--font-size-sm)',
                    cursor: 'pointer', marginBottom: attachments.length ? 'var(--space-3)' : 0,
                    background: dragOver ? 'var(--color-surface-hover, rgba(0,0,0,0.02))' : 'transparent',
                }}
            >
                Drag & drop files here, or click to browse · images & documents, up to 25 MB
            </div>}
            {readOnly && !loading && attachments.length === 0 && (
                <p className="text-muted" style={{ fontSize: 'var(--font-size-sm)', margin: 0 }}>No files attached.</p>
            )}

            {loading ? (
                <p className="text-muted" style={{ fontSize: 'var(--font-size-sm)' }}>Loading…</p>
            ) : attachments.length === 0 ? null : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
                    {attachments.map(att => (
                        <div
                            key={att.id}
                            style={{
                                display: 'flex', alignItems: 'center', gap: 'var(--space-2)',
                                padding: 'var(--space-2)', border: '1px solid var(--color-border)', borderRadius: 8,
                            }}
                        >
                            <Thumbnail taskId={taskId} att={att} onClick={() => handleDownload(att)} />
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <button
                                    onClick={() => handleDownload(att)}
                                    title={`Download ${att.filename}`}
                                    style={{
                                        background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                                        color: 'var(--color-teal)', fontWeight: 600, fontSize: 'var(--font-size-sm)',
                                        textAlign: 'left', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                                        maxWidth: '100%', display: 'block',
                                    }}
                                >
                                    {att.filename}
                                </button>
                                <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>
                                    {formatSize(att.file_size)}
                                    {att.uploader_name ? ` · ${att.uploader_name}` : ''}
                                </div>
                            </div>
                            {!readOnly && <button
                                onClick={() => handleDelete(att)}
                                title="Delete attachment"
                                aria-label={`Delete ${att.filename}`}
                                style={{
                                    background: 'none', border: 'none', cursor: 'pointer',
                                    color: 'var(--color-text-muted)', flexShrink: 0,
                                    display: 'inline-flex', alignItems: 'center',
                                }}
                            >
                                <Trash2 size={16} aria-hidden="true" />
                            </button>}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
